/* @HEADER
 * @version 1.1.171 | 2026-10-17
 * @purpose Pure (React-free) resolver that maps the current location (URL hash first, real pathname as fallback) to an unauthenticated auth-flow route, plus the shouldShowLanding gate decision. Split out of App.tsx so both can be unit-tested without a DOM/React environment (ZVX-DEV-2412).
 * @sidecar unauthRouteResolve.ts.header.md
 * @layer app | @hex _none_ | @ctx routing
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-1344, ZVX-DEV-1349, ZVX-DEV-1351, ZVX-DEV-2376, ZVX-DEV-2412, ZVX-DEV-2591, ZVX-DEV-2623

/**
 * The unauthenticated auth-flow routes App.tsx dispatches to. `null` means
 * "no recognised auth route" — the caller falls through to the SaaS landing
 * gate / AuthenticatedApp.
 */
export type UnauthRoute =
  | 'register'
  | 'check-email'
  | 'verify-email'
  | 'login'
  | 'accept-invite'
  | 'forgot-password'
  | 'reset-password'
  | 'owner-invite'
  | 'legal-privacy'
  | 'legal-terms'
  | 'legal-license'
  | 'legal-marketing'
  | null

export interface UnauthRouteState {
  route: UnauthRoute
  email: string
  token: string
  next: string
  /** `j=<ru|generic>` query param carried by the `legal-*` routes (ZVX-DEV-2756). Empty for all other routes. */
  jurisdiction: string
  /**
   * `invite=<token>` query param carried by the `verify-email` route
   * (ZVX-DEV-2892). Populated only when the verification email link itself
   * embedded a pending org-invite token (apps/zvenix-api/routes/auth-register.mjs).
   * Device-independent alternative to pendingInviteToken.ts's localStorage
   * stash — a brand-new invitee who verifies on a DIFFERENT device/browser
   * than the one that registered would otherwise never resume the accept
   * flow at all. Empty for every other route.
   */
  inviteToken: string
}

const EMPTY_STATE: UnauthRouteState = {
  route: null,
  email: '',
  token: '',
  next: '',
  jurisdiction: '',
  inviteToken: '',
}

/**
 * ZVX-DEV-2756: public legal document hash routes. Each carries an optional
 * `j=<ru|generic>` query param so the page can render the same jurisdiction
 * the visitor was seeing on the registration form; defaults to `generic`.
 */
const LEGAL_HASH_ROUTES: Readonly<Record<string, Exclude<UnauthRoute, null>>> = {
  '#legal-privacy': 'legal-privacy',
  '#legal-terms': 'legal-terms',
  '#legal-license': 'legal-license',
  '#legal-marketing': 'legal-marketing',
}

/**
 * Real pathname → UnauthRoute map. These are the paths the API server
 * actually embeds in outbound emails (ZVX-DEV-2412 root cause):
 *   - `POST /auth/register`         → link uses `${origin}/verify-email?token=...`
 *   - `POST /auth/forgot-password`  → link uses `${origin}/reset-password?token=...`
 * (see apps/zvenix-api/routes/auth-register.mjs and auth-forgot-password.mjs).
 * `/accept-invite` is emailed as a HASH link (`/#accept-invite?token=...`) so it
 * is already covered by the hash-based resolver below, but is listed here too
 * so a bookmarked/shared plain path still works and the landing gate never
 * swallows it.
 *
 * ZVX-DEV-2623: `/owner-invite` is the renamed unified-invitation-terminology
 * path for the self-service binding-token invite (was `/claim`, ZVX-DEV-2591).
 * `/claim` is KEPT here as a backward-compat alias — links already issued
 * and live in prod before this rename must keep resolving.
 */
const AUTH_FLOW_PATH_ROUTES: Readonly<Record<string, Exclude<UnauthRoute, null>>> = {
  '/register': 'register',
  '/check-email': 'check-email',
  '/verify-email': 'verify-email',
  '/login': 'login',
  '/accept-invite': 'accept-invite',
  '/forgot-password': 'forgot-password',
  '/reset-password': 'reset-password',
  '/owner-invite': 'owner-invite',
  '/claim': 'owner-invite',
}

/**
 * Path prefixes that must never be swallowed by the SaaS landing gate for an
 * anonymous visitor, regardless of whether the (legacy, hash-based) client
 * router already recognises them. This is the allowlist requested by
 * ZVX-DEV-2412 — a single bounded source of truth instead of scattered path
 * checks.
 */
export const AUTH_FLOW_PATH_PREFIXES: readonly string[] = Object.keys(AUTH_FLOW_PATH_ROUTES)

/** True when `pathname` is (or is nested under) a known auth-flow path. */
export function isAuthFlowPath(pathname: string): boolean {
  return AUTH_FLOW_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix + '/'))
}

/**
 * Resolve an UnauthRouteState from a URL hash string (`window.location.hash`).
 * Mirrors the CTA-driven navigation used inside the app (`navigateRoute`,
 * `window.location.hash = '#login'`, etc). Returns `null` when the hash does
 * not match any recognised auth-flow hash.
 */
function resolveFromHash(hash: string): UnauthRouteState | null {
  if (hash.startsWith('#register')) {
    // ZVX-DEV-2843: register-first is now the default landing for an
    // unauthenticated invitee (AcceptInvitePage.onNeedsSignIn redirects here
    // with next=accept-invite&token=<inviteToken>). Parse both the same way
    // '#login' does so the invite token survives arriving at the register
    // screen — it is persisted client-side (pendingInviteToken.ts) from
    // there because the register -> verify-email email round trip cannot
    // otherwise carry it through.
    const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
    const params = new URLSearchParams(qs)
    return {
      route: 'register',
      email: '',
      token: params.get('token') ?? '',
      next: params.get('next') ?? '',
      jurisdiction: '',
      inviteToken: '',
    }
  }
  if (hash.startsWith('#check-email')) {
    const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
    const params = new URLSearchParams(qs)
    // ZVX-DEV-7534: an invitee's registration carries next+token here so that
    // «back to registration» returns to a #register that still has them.
    return {
      route: 'check-email',
      email: params.get('email') ?? '',
      token: params.get('token') ?? '',
      next: params.get('next') ?? '',
      jurisdiction: '',
      inviteToken: '',
    }
  }
  if (hash.startsWith('#verify-email')) {
    const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
    const params = new URLSearchParams(qs)
    // ZVX-DEV-2892 — `invite=<token>` rides the legacy hash-based
    // verify-email link the same way it rides the PATH-based link below.
    return {
      route: 'verify-email',
      email: '',
      token: params.get('token') ?? '',
      next: '',
      jurisdiction: '',
      inviteToken: params.get('invite') ?? '',
    }
  }
  if (hash.startsWith('#login')) {
    const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
    const params = new URLSearchParams(qs)
    return {
      route: 'login',
      email: '',
      token: params.get('token') ?? '',
      next: params.get('next') ?? '',
      jurisdiction: '',
      inviteToken: '',
    }
  }
  if (hash.startsWith('#accept-invite')) {
    const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
    const params = new URLSearchParams(qs)
    return { route: 'accept-invite', email: '', token: params.get('token') ?? '', next: '', jurisdiction: '', inviteToken: '' }
  }
  if (hash.startsWith('#forgot-password')) {
    return { route: 'forgot-password', email: '', token: '', next: '', jurisdiction: '', inviteToken: '' }
  }
  if (hash.startsWith('#reset-password')) {
    const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
    const params = new URLSearchParams(qs)
    return { route: 'reset-password', email: '', token: params.get('token') ?? '', next: '', jurisdiction: '', inviteToken: '' }
  }
  // ZVX-DEV-2623: '#owner-invite' is the renamed primary hash; '#claim' is
  // kept as a backward-compat alias so links minted before the rename
  // (already emailed/shared, live in prod) keep resolving to the same route.
  if (hash.startsWith('#owner-invite') || hash.startsWith('#claim')) {
    const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
    const params = new URLSearchParams(qs)
    return { route: 'owner-invite', email: '', token: params.get('token') ?? '', next: '', jurisdiction: '', inviteToken: '' }
  }
  // ZVX-DEV-2756: public legal document pages. `j=<ru|generic>` is optional;
  // an empty value means "let the page fall back to generic".
  for (const [prefix, route] of Object.entries(LEGAL_HASH_ROUTES)) {
    if (hash.startsWith(prefix)) {
      const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
      const params = new URLSearchParams(qs)
      return { route, email: '', token: '', next: '', jurisdiction: params.get('j') ?? '', inviteToken: '' }
    }
  }
  return null
}

/**
 * Resolve an UnauthRouteState from the real `pathname` + `search` (the shape
 * outbound emails actually link to — ZVX-DEV-2412). Returns `null` when the
 * pathname is not a known auth-flow path.
 */
function resolveFromPath(pathname: string, search: string): UnauthRouteState | null {
  const route = AUTH_FLOW_PATH_ROUTES[pathname]
  if (!route) return null
  const params = new URLSearchParams(search)
  switch (route) {
    case 'check-email':
      return { route, email: params.get('email') ?? '', token: '', next: '', jurisdiction: '', inviteToken: '' }
    case 'verify-email':
      // ZVX-DEV-2892 — `invite=<token>` rides the PATH-based verify-email
      // link the API server actually emails (auth-register.mjs), making the
      // register -> verify accept flow device-independent.
      return {
        route,
        email: '',
        token: params.get('token') ?? '',
        next: '',
        jurisdiction: '',
        inviteToken: params.get('invite') ?? '',
      }
    case 'accept-invite':
    case 'reset-password':
    case 'owner-invite':
      return { route, email: '', token: params.get('token') ?? '', next: '', jurisdiction: '', inviteToken: '' }
    case 'login':
      return {
        route,
        email: '',
        token: params.get('token') ?? '',
        next: params.get('next') ?? '',
        jurisdiction: '',
        inviteToken: '',
      }
    default:
      return { route, email: '', token: '', next: '', jurisdiction: '', inviteToken: '' }
  }
}

/**
 * Resolve the effective unauthenticated route from the current location.
 * The hash is checked first (existing in-app CTA navigation), the real
 * pathname is checked as a fallback (the shape of the links the API server
 * actually emails — ZVX-DEV-2412). Returns the "no auth route" empty state
 * when neither matches.
 */
export function resolveUnauthRoute(hash: string, pathname: string, search: string): UnauthRouteState {
  return resolveFromHash(hash) ?? resolveFromPath(pathname, search) ?? EMPTY_STATE
}

/**
 * SaaS landing gate decision (ZVX-DEV-2376, hardened by ZVX-DEV-2412,
 * corrected by ZVX-DEV-2670).
 *
 * Returns true only when the visitor should see the marketing landing page
 * instead of the routed app: online mode is on, the visitor is anonymous, and
 * the current pathname is not one of the unauthenticated auth-flow paths
 * (verify-email, reset-password, etc).
 *
 * ZVX-DEV-2670: `hasLicense` is intentionally IGNORED while `onlineMode` is
 * true. A previously-imported offline license must never keep a logged-out
 * (anonymous) visitor inside the app once they hit an ONLINE_MODE deployment
 * — offline licenses only admit the air-gap/offline-single-file build, where
 * `onlineMode` is false and there is no server-backed auth to fall back to.
 * Do not re-add `!hasLicense` to the online-mode branch below; that was the
 * exact regression (an imported license kept a logged-out online user inside
 * the app instead of the marketing landing).
 *
 * This is a defense-in-depth check: `resolveUnauthRoute` already short-circuits
 * App() before this gate is ever evaluated for a recognised auth path, but
 * keeping the same allowlist here means the gate can never regress
 * independently of the route resolver.
 */
export function shouldShowLanding(params: {
  pathname: string
  onlineMode: boolean
  authenticated: boolean
  hasLicense: boolean
}): boolean {
  // Non-online (air-gap / offline single-file) builds never show the SaaS
  // landing — the offline license (or lack of one) is handled entirely
  // inside AuthenticatedApp, not by this gate.
  if (!params.onlineMode) return false
  if (isAuthFlowPath(params.pathname)) return false
  // Online (SaaS) build: auth is the only gate. An offline license never
  // admits a logged-out visitor here — see ZVX-DEV-2670 note above.
  return !params.authenticated
}
